import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class GenericPayloadService {
  constructor(protected http: HttpClient) { }

  private router = inject(Router);

  private getPayloadOperator(attributeName: string, values: any[], currentOperator?: string): string {
    const normalizedKey = String(attributeName || '').trim().toLowerCase();
    if (['year', 'qtr', 'quarter'].includes(normalizedKey)) {
      return 'IN';
    }

    if (currentOperator && !['=', 'IN'].includes(currentOperator)) {
      return currentOperator;
    }

    return values.length > 1 ? 'IN' : '=';
  }

  private normalizeWhere(where: any[]): any[] {
    if (!Array.isArray(where) || where.length === 0) {
      return [];
    }

    const hasMonth = where.some(
      (item: any) => String(item?.attributeName || '').trim().toLowerCase() === 'month',
    );
    const timeFilterOrder = new Map([
      ['month', 0],
      ['year', 1],
      ['qtr', 2],
      ['quarter', 2],
    ]);

    const hasExplicitOr = where.some(
      (item: any) => String(item?.logicalOperator || '').trim().toUpperCase() === 'OR',
    );
    const normalized = where
      .map((item: any) => {
        const attributeValues = Array.isArray(item?.attributeValues)
          ? item.attributeValues
          : [item?.attributeValues].filter(
              (value) => value !== undefined && value !== null && value !== '',
            );

        return {
          ...item,
          attributeValues,
          operator: this.getPayloadOperator(
            item?.attributeName,
            attributeValues,
            item?.operator,
          ),
        };
      })
      .sort((a: any, b: any) => {
        if (!hasMonth || hasExplicitOr) {
          return 0;
        }

        const keyA = String(a?.attributeName || '').trim().toLowerCase();
        const keyB = String(b?.attributeName || '').trim().toLowerCase();
        return (timeFilterOrder.get(keyA) ?? 99) - (timeFilterOrder.get(keyB) ?? 99);
      });

    if (!hasExplicitOr) {
      normalized.forEach((item: any, index: number) => {
        item.logicalOperator = index < normalized.length - 1 ? 'AND' : '';
      });
    }

    return normalized;
  }

  buildPayload(requestParamFilters: any, staticFilters: any[] = []): any {
    const currentPath = this.router.url.split('?')[0];
    const isQuarterly = currentPath.toLowerCase().includes('quarterly');
    
    let pairedFilters: any[] = [];
    let processedFilters = { ...requestParamFilters };

    if (isQuarterly) {
      const yearValues = requestParamFilters['year'];
      // Handle both 'qtr' and 'quarter' keys
      const qtrKey = 'qtr' in requestParamFilters ? 'qtr' : ('quarter' in requestParamFilters ? 'quarter' : null);
      const qtrValues = qtrKey ? requestParamFilters[qtrKey] : null;

      // If both are arrays of same length > 1, pair them
      if (Array.isArray(yearValues) && Array.isArray(qtrValues) && yearValues.length === qtrValues.length && yearValues.length > 1) {
        for (let i = 0; i < yearValues.length; i++) {
          const isLastPair = i === yearValues.length - 1;
          pairedFilters.push({
            attributeName: 'year',
            operator: 'IN',
            attributeValues: [String(yearValues[i])],
            logicalOperator: 'AND'
          });
          pairedFilters.push({
            attributeName: qtrKey,
            operator: 'IN',
            attributeValues: [String(qtrValues[i])],
            logicalOperator: isLastPair ? '' : 'OR'
          });
        }
        // Remove handled keys from processed filters
        delete processedFilters['year'];
        if (qtrKey) delete processedFilters[qtrKey];
      }
    }

    const dynamicFilters = Object.entries(processedFilters)
      .map(([key, value]) => {
        const rawValues = Array.isArray(value) ? value.flat(Infinity) : [value];
        const updatedValues = rawValues.filter(v =>
          v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0)
        );

        if (updatedValues.length === 0) return null;

        return {
          attributeName: key,
          operator: this.getPayloadOperator(key, updatedValues),
          attributeValues: updatedValues,
          logicalOperator: 'AND'
        };
      })
      .filter((f): f is NonNullable<typeof f> => f !== null);

    let finalWhere = [...staticFilters, ...dynamicFilters];
    
    if (pairedFilters.length > 0) {
      // Connect previous filters to the paired sequence
      if (finalWhere.length > 0) {
        finalWhere[finalWhere.length - 1].logicalOperator = 'AND';
        finalWhere = [...finalWhere, ...pairedFilters];
      } else {
        finalWhere = pairedFilters;
      }
    } else {
      // For standard filters, clear the last logicalOperator
      if (finalWhere.length > 0) finalWhere[finalWhere.length - 1].logicalOperator = '';
    }

    return {
      filter: {
        where: this.normalizeWhere(finalWhere),
        join: {},
        orderBy: {}
      }
    };
  }

  buildQueryParams(queryParamFilters: any): HttpParams {
    let params = new HttpParams();
    for (const key in queryParamFilters) {
      const value = queryParamFilters[key];

      if (key === 'slider_range' && Array.isArray(value) && value.length === 2) {
        const start = parseInt(value[0], 10);
        const end = parseInt(value[1], 10);
        params = params.set('startRange', start.toString());
        params = params.set('endRange', end.toString());
      } else if (key === 'button') {
        params = params.set(value[0], true);
      } else {
        params = params.set(key, Array.isArray(value) ? value.join(',') : value);
      }
    }

    return params;
  }

  sendRequest(url: string, payload: any, method: 'post' | 'get', queryParams: HttpParams): Observable<any> {
    return method === 'post' ? this.http.post(url, payload, { params: queryParams }) : this.http.get(url, { params: queryParams });
  }
}
